import { Game, CalendarEvent } from '../types';
import { EventType, getEventType, createCentralTimeDate, isLeagueGame, isHomeGame, isTournamentEvent } from './eventUtils';
import { getRink, Rink } from './locationUtils';

// --- Type definitions for conflicts ---
export interface DiscrepancyConflict {
    type: 'discrepancy';
    leagueGame: Game;
    calendarGame: CalendarEvent;
    timeMismatch: boolean;
    locationMismatch: boolean;
}

export interface MissingFromCalendarConflict {
    type: 'missing_from_calendar';
    leagueGame: Game;
}

export interface MissingFromLeagueConflict {
    type: 'missing_from_league';
    calendarGame: CalendarEvent;
}

export interface MissingVolunteersConflict {
    type: 'missing_volunteers';
    calendarGame: CalendarEvent;
    missingDuties?: string[];
}

export type ScheduleConflict =
    | DiscrepancyConflict
    | MissingFromCalendarConflict
    | MissingFromLeagueConflict
    | MissingVolunteersConflict;

interface BestMatch {
    game: CalendarEvent;
    index: number;
    diff: number;
}

export function getLeagueGameTime(leagueGame: Game): Date {
    return createCentralTimeDate(leagueGame.date, leagueGame.time);
}

export const getOpponentFromSummary = (summary: string): string | null => {
    if (!summary) return null;
    const parts = summary.split(' at ');
    if (parts.length !== 2) return null;
    const team1 = parts[0].trim();
    const team2 = parts[1].trim();
    // CSDHL calendar specifies our name as 'Jets' where our calendar specifies
    // us as '2014 CSDHL.'
    if (team1.includes("Jets") || team1.includes("2014 CSDHL")) return team2;
    if (team2.includes("Jets") || team2.includes("2014 CSDHL")) return team1;
    return null;
};

export const getOpponentFromGame = (game: Game): string => {
    // In the league schedule, the team is always "Chicago Jets 2014"
    const teamName = "Jets";
    return game.homeTeam.name.includes(teamName) ? game.visitorTeam.name : game.homeTeam.name;
};

/**
 * Finds all schedule discrepancies, missing calendar games, extra league games,
 * and upcoming games with missing volunteer duties.
 */
export function findScheduleConflicts(
    calendarEvents: CalendarEvent[],
    fullSchedule: Game[],
    leagueTeamNames: Set<string>,
    now: Date = new Date()
): ScheduleConflict[] {
    const calendarGames = calendarEvents.filter(event => getEventType(event) === EventType.Game);
    const leagueGames = fullSchedule.filter(game =>
        (game.homeTeam.name.includes('Jets') || game.visitorTeam.name.includes('Jets'))
    );

    const foundConflicts: ScheduleConflict[] = [];
    const matchedCalendarGameIndices = new Set<number>();

    for (const leagueGame of leagueGames) {
        const leagueDate = getLeagueGameTime(leagueGame);
        if (leagueDate < now) continue;

        const potentialMatches: BestMatch[] = [];

        calendarGames.forEach((calGame, index) => {
            if (matchedCalendarGameIndices.has(index)) return;
            const calDate = new Date(calGame.start);
            const timeDiff = Math.abs(leagueDate.getTime() - calDate.getTime());
            if (timeDiff < 12 * 60 * 60 * 1000) { // 12-hour window to find match on same day
                potentialMatches.push({ game: calGame, index, diff: timeDiff });
            }
        });

        if (potentialMatches.length > 0) {
            potentialMatches.sort((a, b) => a.diff - b.diff);
            const bestMatch = potentialMatches[0];
            matchedCalendarGameIndices.add(bestMatch.index);
            const { game: matchingCalendarGame } = bestMatch;

            const timeMismatch = Math.abs(getLeagueGameTime(leagueGame).getTime() - new Date(matchingCalendarGame.start).getTime()) > 60 * 1000;

            const leagueRink = getRink(leagueGame.location);
            const calendarRink = getRink(matchingCalendarGame.location);

            // Locations mismatch if they don't map to the same known rink,
            // or if one is unknown and the raw strings don't match.
            const locationMismatch = leagueRink !== calendarRink || (leagueRink === Rink.Unknown && leagueGame.location !== matchingCalendarGame.location);

            if (timeMismatch || locationMismatch) {
                foundConflicts.push({ type: 'discrepancy', leagueGame, calendarGame: matchingCalendarGame, timeMismatch, locationMismatch });
            }
        } else {
            foundConflicts.push({ type: 'missing_from_calendar', leagueGame });
        }
    }

    calendarGames.forEach((calGame, index) => {
        if (!matchedCalendarGameIndices.has(index)) {
            if (new Date(calGame.start) < now) return;

            const opponent = getOpponentFromSummary(calGame.summary);
            if (opponent && isLeagueGame(calGame, leagueTeamNames)) {
                foundConflicts.push({ type: 'missing_from_league', calendarGame: calGame });
            }
        }
    });

    // Check for missing volunteers on upcoming games
    const upcomingGames = calendarGames.filter(g =>
        new Date(g.start) > now
    );

    upcomingGames.forEach(game => {
        const eventType = getEventType(game);
        const isTournament = eventType === EventType.Tournament || Boolean(game.isTournament) || isTournamentEvent(game);

        // Tournament games do not require volunteer flags. Only regular league games require volunteer flags.
        if (isTournament) return;

        const isRegularGame = eventType === EventType.Game && isLeagueGame(game, leagueTeamNames);
        if (!isRegularGame) return;

        const home = isHomeGame(game);
        const duties = game.duties || {};
        const missingDuties: string[] = [];

        if (home) {
            // Home games require Scoreboard and Gamesheet (no Penalty Box needed)
            if (!duties['Scoreboard']) missingDuties.push('Scoreboard');
            if (!duties['Gamesheet']) missingDuties.push('Gamesheet');
        } else {
            // Away games only require Penalty Box (no Scoreboard or Gamesheet needed)
            if (!duties['Penalty Box'] && !duties['Penalty']) missingDuties.push('Penalty Box');
        }

        if (missingDuties.length > 0) {
            foundConflicts.push({ type: 'missing_volunteers', calendarGame: game, missingDuties });
        }
    });

    return foundConflicts;
}

export interface ConflictSummary {
    total: number;
    hasCalendarIssues: boolean;
    hasVolunteerIssues: boolean;
    calendarConflicts: ScheduleConflict[];
    volunteerConflicts: MissingVolunteersConflict[];
    mismatchCount: number;
    missingCalendarCount: number;
    missingLeagueCount: number;
    volunteersCount: number;
}

export function categorizeConflicts(conflicts: ScheduleConflict[]): ConflictSummary {
    const calendarConflicts: ScheduleConflict[] = [];
    const volunteerConflicts: MissingVolunteersConflict[] = [];
    let mismatchCount = 0;
    let missingCalendarCount = 0;
    let missingLeagueCount = 0;
    let volunteersCount = 0;

    for (const c of conflicts) {
        if (c.type === 'missing_volunteers') {
            volunteerConflicts.push(c);
            volunteersCount++;
        } else {
            calendarConflicts.push(c);
            if (c.type === 'discrepancy') mismatchCount++;
            else if (c.type === 'missing_from_calendar') missingCalendarCount++;
            else if (c.type === 'missing_from_league') missingLeagueCount++;
        }
    }

    return {
        total: conflicts.length,
        hasCalendarIssues: calendarConflicts.length > 0,
        hasVolunteerIssues: volunteerConflicts.length > 0,
        calendarConflicts,
        volunteerConflicts,
        mismatchCount,
        missingCalendarCount,
        missingLeagueCount,
        volunteersCount,
    };
}
